export interface PatientPermissions {
  isOwner: boolean;
  isObserver: boolean;
  isCaregiver: boolean;
  canEdit: boolean;
  canInvite: boolean;
  canManageTeam: boolean;
  canExecuteTasks: boolean;
  canViewDashboard: boolean;
  canEditInventory: boolean;
  /** Delegated caregivers (or owners/managers) can create/edit care plan tasks */
  canEditCarePlan: boolean;
  /** Delegated caregivers (or owners/managers) can create/edit shift templates */
  canManageSchedules: boolean;
  /** Can define which caregiver covers which shift template */
  canAssignShiftTemplates: boolean;
  /** Whether this user holds the delegation flag */
  isCareConfigDelegate: boolean;
}

/**
 * Derives patient-level permissions from a direct patient role,
 * an optional company role and an optional delegation flag
 * (`patient_user_links.can_manage_care_config`).
 */
export function derivePermissions(
  role: string | null,
  companyRole: string | null,
  canManageCareConfig: boolean = false
): PatientPermissions {
  const isOwner = role === 'owner';
  const isObserver = role === 'observer';
  const isCaregiver = role === 'caregiver';
  const isCompanyManager = companyRole === 'manager';
  const isCompanyCoordinator = companyRole === 'coordinator';
  const isCompanyMgrOrCoord = isCompanyManager || isCompanyCoordinator;
  const isCompanyEmployee = companyRole === 'employee';
  const isCareConfigDelegate = isCaregiver && canManageCareConfig;

  return {
    isOwner,
    isObserver,
    isCaregiver,
    canEdit: isOwner || isCompanyMgrOrCoord,
    canInvite: isOwner,
    canManageTeam: isOwner || isCompanyMgrOrCoord,
    canExecuteTasks: isCaregiver,
    canViewDashboard: isOwner || isObserver || isCompanyMgrOrCoord,
    canEditInventory: isOwner || isCompanyMgrOrCoord || isCompanyEmployee,
    canEditCarePlan: isOwner || isCompanyMgrOrCoord || isCareConfigDelegate,
    canManageSchedules: isOwner || isCompanyMgrOrCoord || isCareConfigDelegate,
    canAssignShiftTemplates: isOwner || isCompanyMgrOrCoord || isCareConfigDelegate,
    isCareConfigDelegate,
  };
}
